import {
  DAILY_STATUS_BLOCK_SIZE,
  DAILY_STATUS_ROW_HEIGHT,
  HISTORY_NAME_COLUMN_DEFAULT_WIDTH_PX,
} from '@/features/shared/components/entityDailyHistory/EntityDailyHistoryPanel.constants';
import { EntityDailyHistoryPanelSkeleton } from '@/features/shared/components/entityDailyHistory/EntityDailyHistoryPanelSkeleton';
import {
  TABLE_GRID_HEADER_HEIGHT,
  TABLE_PANEL_HEADER_HEIGHT,
  TABLE_ROW_HEIGHT,
} from '@/features/shared/components/tableSystem/tableConstants';
import { buildTableDividerPseudoSx } from '@/features/shared/components/tableSystem/tableDividerSx';
import { tablePanelSurfaceSx } from '@/features/shared/components/tableSystem/tablePanelSurfaceSx';
import {
  inertTablePanelTabSx,
  tablePanelTabsSx,
} from '@/features/shared/components/tableSystem/tablePanelTabs';
import {
  buildToolbarIconButtonSx,
  buildToolbarIconCircleSx,
} from '@/features/shared/components/tableSystem/tableToolbar.helpers';
import { componentSizes } from '@/theme/componentSizes';
import { isLightTheme } from '@/theme/semanticTokens';
import { s } from '@/theme/tokens';
import { SkeletonDataGrid } from '@/ui/composites/grid/SkeletonDataGrid';
import { SkeletonPaginationFooter } from '@/ui/composites/grid/SkeletonPaginationFooter';
import { Panel } from '@/ui/composites/Panel';
import { Stack } from '@/ui/layout/Stack';
import {
  Box,
  Cancel,
  IconButton,
  PanelHeadingText,
  Skeleton,
  Tab,
  Tabs,
  useTheme,
} from '@/ui/primitives';
import {
  ChecklistRtlOutlinedIcon,
  FlakyIcon,
  ReplayOutlinedIcon,
  SortOutlinedIcon,
  TimerOutlinedIcon,
  ViewColumnOutlinedIcon,
} from '@/ui/primitives/icons';
import {
  deferredTableColumnsByVariant,
  type DeferredSkeletonRow,
  type DeferredTablePanelSkeletonVariant,
} from './DeferredTableSkeletonColumns';
import type { ReactElement } from 'react';

const DEFERRED_HEADER_SKELETON_HEIGHT = componentSizes.dataGrid.skeletonHeight;
const DEFERRED_HEADER_SKELETON_RADIUS = '999px';
const DEFERRED_TOOLBAR_SKELETON_WIDTH = 128;
const DEFERRED_TOOLBAR_ICON_SIZE = 32;
const DEFERRED_HISTORY_NAME_COLUMN_WIDTH = `${String(HISTORY_NAME_COLUMN_DEFAULT_WIDTH_PX)}px`;

const DISABLED_TOOLBAR_ICON_SX = {
  width: '22px !important',
  height: '22px !important',
} as const;

const DISABLED_TOOLBAR_COMPACT_ICON_SX = {
  width: '19px !important',
  height: '19px !important',
} as const;

type DeferredTablePanelSkeletonProps = {
  rowCount?: number;
  titleWidth?: number;
  variant?: DeferredTablePanelSkeletonVariant;
  embedded?: boolean;
  hidden?: boolean;
};

export const DeferredTablePanelSkeleton = ({
  rowCount = 5,
  titleWidth = 132,
  variant = 'default',
  embedded = false,
  hidden = false,
}: DeferredTablePanelSkeletonProps): ReactElement => {
  const content = embedded ? (
    <SkeletonDataGrid<DeferredSkeletonRow>
      columns={deferredTableColumnsByVariant[variant]}
      loadingRowCount={rowCount}
      pageSizeOptions={[rowCount]}
      pageSize={rowCount}
      rowHeight={TABLE_ROW_HEIGHT}
      getRowId={(row) => row.id}
      autoHeight
    />
  ) : (
    <Panel
      padding="md"
      gap="none"
      contentGap="md"
      headerGap="none"
      headerSlot={
        <Box
          sx={{
            width: '100%',
            minWidth: 0,
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: `${TABLE_PANEL_HEADER_HEIGHT}px`,
          }}
        >
          <Stack
            direction="row"
            align="flex-end"
            justify="space-between"
            gap="sm"
            sx={{
              minWidth: 0,
              minHeight: `${TABLE_PANEL_HEADER_HEIGHT}px`,
              height: `${TABLE_PANEL_HEADER_HEIGHT}px`,
              px: 'md',
            }}
          >
            <Skeleton
              variant="rounded"
              width={titleWidth}
              height={DEFERRED_HEADER_SKELETON_HEIGHT}
              sx={{ borderRadius: DEFERRED_HEADER_SKELETON_RADIUS, transform: 'none' }}
            />
            <Skeleton
              variant="rounded"
              width={DEFERRED_TOOLBAR_SKELETON_WIDTH}
              height={DEFERRED_HEADER_SKELETON_HEIGHT}
              sx={{ borderRadius: DEFERRED_HEADER_SKELETON_RADIUS, transform: 'none' }}
            />
          </Stack>
        </Box>
      }
      sx={tablePanelSurfaceSx}
    >
      <SkeletonDataGrid<DeferredSkeletonRow>
        columns={deferredTableColumnsByVariant[variant]}
        loadingRowCount={rowCount}
        pageSizeOptions={[rowCount]}
        pageSize={rowCount}
        rowHeight={TABLE_ROW_HEIGHT}
        getRowId={(row) => row.id}
        autoHeight
      />
    </Panel>
  );

  return hidden ? (
    <Box aria-hidden="true" sx={{ visibility: 'hidden' }}>
      {content}
    </Box>
  ) : (
    content
  );
};

type DeferredHistoryPanelSkeletonProps = {
  dayCount?: number;
  rowCount?: number;
  title?: string;
  hidden?: boolean;
};

type DeferredTabbedTablePanelSkeletonProps = {
  activeTabLabel: string;
  inactiveTabLabel: string;
  variant: DeferredTablePanelSkeletonVariant;
  rowCount?: number;
};

const DisabledPanelToolbarIcon = ({ children }: { children: ReactElement }): ReactElement => {
  const theme = useTheme();
  const toolbarInactiveIconColor = isLightTheme(theme) ? 'text.secondary' : 'common.white';
  const toolbarIconButtonSx = buildToolbarIconButtonSx(
    toolbarInactiveIconColor,
    DEFERRED_TOOLBAR_ICON_SIZE,
  );

  return (
    <IconButton size="medium" disabled tabIndex={-1} aria-hidden="true" sx={toolbarIconButtonSx}>
      <Box sx={buildToolbarIconCircleSx(false)}>{children}</Box>
    </IconButton>
  );
};

const DeferredHistoryToolbarIcons = (): ReactElement => (
  <Stack direction="row" align="center" gap="sm">
    <DisabledPanelToolbarIcon>
      <SortOutlinedIcon sx={DISABLED_TOOLBAR_ICON_SX} />
    </DisabledPanelToolbarIcon>
    <DisabledPanelToolbarIcon>
      <Cancel sx={DISABLED_TOOLBAR_ICON_SX} />
    </DisabledPanelToolbarIcon>
  </Stack>
);

const DeferredTabbedTableToolbarIcons = (): ReactElement => (
  <Stack direction="row" align="center" gap="sm">
    <DisabledPanelToolbarIcon>
      <ViewColumnOutlinedIcon sx={DISABLED_TOOLBAR_ICON_SX} />
    </DisabledPanelToolbarIcon>
    <DisabledPanelToolbarIcon>
      <ChecklistRtlOutlinedIcon sx={DISABLED_TOOLBAR_ICON_SX} />
    </DisabledPanelToolbarIcon>
    <DisabledPanelToolbarIcon>
      <ReplayOutlinedIcon sx={DISABLED_TOOLBAR_COMPACT_ICON_SX} />
    </DisabledPanelToolbarIcon>
    <DisabledPanelToolbarIcon>
      <TimerOutlinedIcon sx={DISABLED_TOOLBAR_COMPACT_ICON_SX} />
    </DisabledPanelToolbarIcon>
    <DisabledPanelToolbarIcon>
      <FlakyIcon sx={DISABLED_TOOLBAR_COMPACT_ICON_SX} />
    </DisabledPanelToolbarIcon>
    <DisabledPanelToolbarIcon>
      <SortOutlinedIcon sx={DISABLED_TOOLBAR_ICON_SX} />
    </DisabledPanelToolbarIcon>
    <DisabledPanelToolbarIcon>
      <Cancel sx={DISABLED_TOOLBAR_ICON_SX} />
    </DisabledPanelToolbarIcon>
  </Stack>
);

export const DeferredHistoryPanelSkeleton = ({
  dayCount = 7,
  rowCount = 5,
  title = 'History',
  hidden = false,
}: DeferredHistoryPanelSkeletonProps): ReactElement => {
  const content = (
    <Panel
      padding="md"
      gap="none"
      contentGap="none"
      headerGap="none"
      headerSlot={
        <Box
          sx={{
            width: '100%',
            minWidth: 0,
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: `${TABLE_PANEL_HEADER_HEIGHT}px`,
          }}
        >
          <Stack
            direction="row"
            align="center"
            justify="space-between"
            gap="md"
            sx={{
              minWidth: 0,
              minHeight: `${TABLE_PANEL_HEADER_HEIGHT}px`,
              height: `${TABLE_PANEL_HEADER_HEIGHT}px`,
            }}
          >
            <Box
              sx={{
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                pl: s('md'),
              }}
            >
              <PanelHeadingText>{title}</PanelHeadingText>
            </Box>
            <Box sx={{ ml: 'auto', pr: s('sm') }}>
              <DeferredHistoryToolbarIcons />
            </Box>
          </Stack>
        </Box>
      }
      sx={tablePanelSurfaceSx}
    >
      <EntityDailyHistoryPanelSkeleton
        dayCount={dayCount}
        entityLabel="Suite"
        entityHeaderContent={null}
        rowCount={rowCount}
        nameColumnWidth={DEFERRED_HISTORY_NAME_COLUMN_WIDTH}
        rowHeight={DAILY_STATUS_ROW_HEIGHT}
        blockSize={DAILY_STATUS_BLOCK_SIZE}
        headerHeight={TABLE_GRID_HEADER_HEIGHT + 14}
        dayHeaderGridRef={{ current: null }}
        nameColumnHeaderCellRef={{ current: null }}
        onNameColumnResizeStart={() => undefined}
        onNameColumnResizeKeyDown={() => undefined}
      />
      <SkeletonPaginationFooter />
    </Panel>
  );

  return hidden ? (
    <Box aria-hidden="true" sx={{ visibility: 'hidden' }}>
      {content}
    </Box>
  ) : (
    content
  );
};

export const DeferredTabbedTablePanelSkeleton = ({
  activeTabLabel,
  inactiveTabLabel,
  variant,
  rowCount = 5,
  hidden = false,
}: DeferredTabbedTablePanelSkeletonProps & { hidden?: boolean }): ReactElement => {
  const content = (
    <Panel
      padding="md"
      gap="none"
      contentGap="md"
      headerGap="none"
      headerSlot={
        <Box
          sx={{
            borderBottom: 'none',
            minHeight: `${TABLE_PANEL_HEADER_HEIGHT}px`,
            position: 'relative',
            '&::after': buildTableDividerPseudoSx('bottom'),
          }}
        >
          <Stack
            direction="row"
            align="center"
            justify="space-between"
            gap="md"
            sx={{
              minWidth: 0,
              minHeight: `${TABLE_PANEL_HEADER_HEIGHT}px`,
              height: `${TABLE_PANEL_HEADER_HEIGHT}px`,
            }}
          >
            <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
              <Tabs
                value={activeTabLabel}
                aria-label={`${activeTabLabel} panel tabs`}
                sx={tablePanelTabsSx}
              >
                <Tab
                  label={activeTabLabel}
                  value={activeTabLabel}
                  disableRipple
                  sx={inertTablePanelTabSx}
                />
                <Tab
                  label={inactiveTabLabel}
                  value={inactiveTabLabel}
                  disableRipple
                  sx={inertTablePanelTabSx}
                />
              </Tabs>
            </Box>
            <Box sx={{ flex: '0 0 auto' }}>
              <Stack
                direction="row"
                align="center"
                gap="sm"
                sx={{
                  minWidth: 0,
                  flexShrink: 0,
                  height: `${TABLE_PANEL_HEADER_HEIGHT}px`,
                  minHeight: `${TABLE_PANEL_HEADER_HEIGHT}px`,
                  boxSizing: 'border-box',
                }}
              >
                <DeferredTabbedTableToolbarIcons />
              </Stack>
            </Box>
          </Stack>
        </Box>
      }
      sx={tablePanelSurfaceSx}
    >
      <SkeletonDataGrid<DeferredSkeletonRow>
        columns={deferredTableColumnsByVariant[variant]}
        loadingRowCount={rowCount}
        pageSizeOptions={[rowCount]}
        pageSize={rowCount}
        rowHeight={TABLE_ROW_HEIGHT}
        getRowId={(row) => row.id}
        autoHeight
      />
    </Panel>
  );

  return hidden ? (
    <Box aria-hidden="true" sx={{ visibility: 'hidden' }}>
      {content}
    </Box>
  ) : (
    content
  );
};
